/**
 * Reconstruct a subscriber's country history from OCS Gy charging events.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `subscriber_country_history` used to read `country.entered` events from the
 * OCS ring buffer, which only ever contains what the Bridge4IP Relay LU webhook
 * pushed into it. Relay is off for reseller 1170 (`relayGy: false`,
 * `relayLU: false`, read via `getResellerInfo`), and the buffer holds 24 hours
 * at best, so the tool answered "no cross-border events" for subscribers that
 * had demonstrably crossed a border.
 *
 * The same answer is already in the OCS itself. `subscriberNetworkEventsOverPeriod`
 * returns `gyAnswer.events` — one Diameter Gy charging record per session
 * Init/Update/Term — and every record carries its own `countryAlpha2`,
 * `countryName`, `operator` and `userLocationInfoMCC`. That is a complete
 * location trail for the queried period, with no webhook and no ring buffer.
 *
 * SHAPE FACTS, captured live 2026-09-06 from ICCID 8948010000020241196
 * (reseller 1170, period 2026-09-01..2026-09-06, 36 Gy events, HTTP 200,
 * `status.code === 0`):
 *
 *   - `usedUnits` is a STRING. Observed values across those 36 records were
 *     "0" (29x), "232", "4267" and — this is the one that bites — "N/A" (5x).
 *     Every Init and most Term records carry "N/A". `Number("N/A")` is NaN, so
 *     a naive sum poisons the whole total to NaN and a naive `> 0` test reads
 *     as false. Both are handled by `gyUsedBytes`.
 *   - `userLocationInfoMCC` is a STRING too ("206", not 206).
 *   - `countryAlpha2` is LOWER-case ("be", "nl"). Callers expect ISO 3166-1
 *     alpha-2 upper-case, so it is upper-cased and, when absent, derived from
 *     the MCC via `mccToIso`.
 *   - Session boundaries interleave. At the NL to BE crossing the BE `Init` was
 *     stamped 2026-09-03T10:19:05.951+02:00 and the NL `Term` that closed the
 *     old session 2026-09-03T10:19:05.955+02:00 — the Term is 4 ms LATER than
 *     the Init that replaced it. Sorting purely by time therefore produces a
 *     phantom one-event NL stay wedged between two BE stays. A `Term` records
 *     where a session ENDED, not a new arrival, so `collapseGyStays` drops a
 *     run made only of `Term` events when the runs on both sides are the same
 *     country, then re-collapses. Without that pass the live capture yields
 *     four stays (NL, BE, NL, BE) instead of the two that actually happened.
 *
 * It lives here, next to `networkEventRows` and `summarizeNetworkEvents` in
 * `diagnose-shapes.ts`, because those parse the other legs of this same
 * response.
 *
 * Everything here is pure. No network, no env, no OCS client.
 */

import { mccToIso } from "@carrier/ocs-spec/mcc-iso";

/** One Gy charging record, as it arrives under `gyAnswer.events`. */
export interface GyEvent {
  eventTime?: unknown;
  countryAlpha2?: unknown;
  countryName?: unknown;
  operator?: unknown;
  userLocationInfoMCC?: unknown;
  userLocationInfoMNC?: unknown;
  usedUnits?: unknown;
  requestType?: unknown;
  [key: string]: unknown;
}

/** A continuous period spent in one country. */
export interface CountryStay {
  /** ISO 3166-1 alpha-2, upper-case. */
  country: string;
  /** OCS `countryName` for the stay, or null when no record carried one. */
  country_name: string | null;
  /** Timestamp of the first Gy record in this country, as OCS wrote it. */
  first_seen: string;
  /** Timestamp of the last Gy record in this country. */
  last_seen: string;
  /** Distinct serving operators seen during the stay, sorted. */
  operators: string[];
  /** Distinct MCCs seen during the stay, sorted. */
  mccs: string[];
  /** Gy records that make up this stay. */
  event_count: number;
  /** Bytes charged during the stay. "N/A" records count as 0, never NaN. */
  used_bytes: number;
}

/** The whole reconstruction for one subscriber. */
export interface GyCountryHistory {
  stays: CountryStay[];
  /** Country changes between consecutive stays. */
  transitions: { from: string; to: string; at: string }[];
  /** Gy records used (those carrying both a timestamp and a country). */
  events_considered: number;
  /** Gy records skipped for want of a timestamp or a resolvable country. */
  events_skipped: number;
  /**
   * Records dropped as session-teardown artefacts — a `Term` stamped after the
   * `Init` that replaced it. Counted here so nothing disappears silently;
   * `events_considered` minus this equals the events across `stays`.
   */
  teardown_artifacts_dropped: number;
}

/**
 * Pull `gyAnswer.events` out of a `subscriberNetworkEventsOverPeriod` response.
 *
 * Accepts the raw OCS envelope (`{ status, subscriberNetworkEventsOverPeriod }`),
 * the unwrapped payload, or a bare `{ gyAnswer }`, because call sites in this
 * repo unwrap to different depths.
 */
export function extractGyEvents(response: unknown): GyEvent[] {
  if (!response || typeof response !== "object") return [];
  const root = response as Record<string, unknown>;

  const candidates: unknown[] = [
    root.subscriberNetworkEventsOverPeriod,
    root.data,
    root,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const gy = (candidate as Record<string, unknown>).gyAnswer;
    if (Array.isArray(gy)) {
      return gy.filter((e): e is GyEvent => !!e && typeof e === "object");
    }
    if (gy && typeof gy === "object") {
      const events = (gy as Record<string, unknown>).events;
      if (Array.isArray(events)) {
        return events.filter((e): e is GyEvent => !!e && typeof e === "object");
      }
    }
  }
  return [];
}

/**
 * Bytes charged on one Gy record.
 *
 * `usedUnits` is a string of bytes, and the OCS writes the literal "N/A" on
 * Init records and on most Terms. Returning 0 for anything non-numeric is what
 * keeps a period total from collapsing to NaN.
 */
export function gyUsedBytes(event: GyEvent): number {
  const raw = event.usedUnits;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  if (typeof raw !== "string") return 0;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed.toUpperCase() === "N/A") return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

/** ISO alpha-2 for a Gy record: `countryAlpha2` first, MCC as the fallback. */
export function gyCountryIso(event: GyEvent): string | null {
  const alpha2 = event.countryAlpha2;
  if (typeof alpha2 === "string" && /^[A-Za-z]{2}$/.test(alpha2.trim())) {
    return alpha2.trim().toUpperCase();
  }
  const mcc = event.userLocationInfoMCC;
  if (typeof mcc === "string" || typeof mcc === "number") return mccToIso(mcc);
  return null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function meaningful(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  const s = str(value);
  return s && s.toUpperCase() !== "N/A" ? s : null;
}

interface NormalisedEvent {
  time: string;
  country: string;
  countryName: string | null;
  operator: string | null;
  mcc: string | null;
  requestType: string;
  bytes: number;
}

function normalise(events: GyEvent[]): { rows: NormalisedEvent[]; skipped: number } {
  const rows: NormalisedEvent[] = [];
  let skipped = 0;

  for (const event of events) {
    const time = str(event.eventTime);
    const country = gyCountryIso(event);
    if (!time || !country) {
      skipped++;
      continue;
    }
    rows.push({
      time,
      country,
      countryName: meaningful(event.countryName),
      operator: meaningful(event.operator),
      mcc: meaningful(event.userLocationInfoMCC),
      requestType: str(event.requestType) ?? "",
      bytes: gyUsedBytes(event),
    });
  }

  // Oldest first. Timestamps are ISO-8601 with an offset, so compare them as
  // instants — a +02:00 record and a Z record from the same moment must not
  // swap places just because their strings sort differently.
  rows.sort((a, b) => {
    const ta = Date.parse(a.time);
    const tb = Date.parse(b.time);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    return a.time.localeCompare(b.time);
  });

  return { rows, skipped };
}

function runToStay(run: NormalisedEvent[]): CountryStay {
  const operators = new Set<string>();
  const mccs = new Set<string>();
  let countryName: string | null = null;
  let bytes = 0;

  for (const row of run) {
    if (row.operator) operators.add(row.operator);
    if (row.mcc) mccs.add(row.mcc);
    if (!countryName && row.countryName) countryName = row.countryName;
    bytes += row.bytes;
  }

  const first = run[0]!;
  const last = run[run.length - 1]!;

  return {
    country: first.country,
    country_name: countryName,
    first_seen: first.time,
    last_seen: last.time,
    operators: [...operators].sort(),
    mccs: [...mccs].sort(),
    event_count: run.length,
    used_bytes: bytes,
  };
}

function collapseRuns(rows: NormalisedEvent[]): NormalisedEvent[][] {
  const runs: NormalisedEvent[][] = [];
  for (const row of rows) {
    const current = runs[runs.length - 1];
    if (current && current[0]!.country === row.country) current.push(row);
    else runs.push([row]);
  }
  return runs;
}

/**
 * Collapse a chronological event list into country stays.
 *
 * Two passes. The first merges consecutive same-country records. The second
 * removes the session-teardown artefact described in the file header: a run
 * made entirely of `Term` records, sitting between two runs of one and the
 * same other country, is the tail of a session that has already been replaced,
 * not a visit. It is dropped and its neighbours re-collapsed.
 */
export function collapseGyStays(events: GyEvent[]): GyCountryHistory {
  const { rows, skipped } = normalise(events);
  if (rows.length === 0) {
    return {
      stays: [],
      transitions: [],
      events_considered: 0,
      events_skipped: skipped,
      teardown_artifacts_dropped: 0,
    };
  }

  let runs = collapseRuns(rows);
  let teardownDropped = 0;

  for (let changed = true; changed; ) {
    changed = false;
    for (let i = 1; i < runs.length - 1; i++) {
      const run = runs[i]!;
      if (!run.every((r) => r.requestType.toLowerCase() === "term")) continue;
      if (runs[i - 1]![0]!.country !== runs[i + 1]![0]!.country) continue;

      teardownDropped += run.length;
      runs = collapseRuns(runs.flatMap((r, idx) => (idx === i ? [] : r)));
      changed = true;
      break;
    }
  }

  const stays = runs.map(runToStay);
  const transitions = stays.slice(1).map((stay, idx) => ({
    from: stays[idx]!.country,
    to: stay.country,
    at: stay.first_seen,
  }));

  return {
    stays,
    transitions,
    events_considered: rows.length,
    events_skipped: skipped,
    teardown_artifacts_dropped: teardownDropped,
  };
}

/** One call: raw OCS response in, country history out. */
export function gyCountryHistoryFromResponse(response: unknown): GyCountryHistory {
  return collapseGyStays(extractGyEvents(response));
}
